/**
 * Agent "create ticket on behalf of a customer" mutation.
 *
 * Targets POST /api/v1/agent/tickets - the agent-side endpoint that
 * accepts an explicit requester (email + name + optional user_id) so
 * a Buyist staff user can file a ticket on a customer's behalf
 * (phone calls, internal escalations, etc.). The customer-side
 * POST /tickets endpoint always uses the authenticated user as the
 * requester and is the wrong target for this flow.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import type {
  AgentTicketCreateRequest,
  TicketResponse,
} from "@/types/api";

export function useCreateTicketAsAgent(): UseMutationResult<
  TicketResponse,
  Error,
  AgentTicketCreateRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AgentTicketCreateRequest) => {
      const response = await api.post<TicketResponse>(
        "/agent/tickets",
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tickets", "list"] });
      void queryClient.invalidateQueries({
        queryKey: ["agent", "tickets"],
      });
    },
  });
}
